import axios from 'axios';
import apiClient from './apiClient';

// Platform related interfaces aligned with OpenAPI specification
export interface UserGroup {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  is_default: boolean;
}

export type PlatformType = 'zoom' | 'dialpad' | 'ringcentral' | 'ring_central' | 'goto' | 'teams' | '8x8' | 'cisco' | 'vonage' | 'nextiva' | 'intermedia' | 'test' | 'testbrowser';

export interface Platform {
  id: number;
  uuid: string;
  name: string;
  platform_type: PlatformType | string; // Allow any string to handle dynamic platform types
  api_url: string;
  mock_api_url?: string;
  production_api_url?: string;
  is_production_enabled: boolean;
  auth_type: 's2s_oauth' | 'user_oauth' | 'api_key';
  auth_client_id?: string;
  auth_client_secret?: string; // writeOnly in API
  auth_account_id?: string;
  auth_url?: string;
  auth_token_url?: string;
  logo_base64?: string; // Base64 encoded logo image
  organization?: number;
  group?: number; // Deprecated - kept for backward compatibility
  is_default: boolean;
  // Browser automation fields
  requires_browser_automation?: boolean;
  gateway_url?: string;
  credential_fields?: {
    [fieldName: string]: {
      label: string;
      type: 'text' | 'password' | 'email' | 'tel';
      required: boolean;
      placeholder?: string;
    };
  };
}

// Legacy interface for backward compatibility
export interface PhonePlatform extends Platform {}

export interface PlatformListResponse {
  count: number;
  results: Platform[];
}

export interface AvailablePlatformType {
  id: string;
  name: string;
  description: string;
}

// Legacy Platform interface for backward compatibility
export interface LegacyPlatform {
  id: number;
  name: string;
  platform_type: string;
  api_url: string;
  is_default: boolean;
  group: string | null;
  specification?: {
    id: number;
    name: string;
  } | null;
}

export interface Extractor {
  id: string; 
  name: string;
  type: string;
  description?: string;
}

export interface CreatePlatformRequest {
  name: string;
  platform_type: 'zoom' | 'dialpad' | 'ring_central';
  mock_api_url?: string;
  production_api_url?: string;
  is_production_enabled?: boolean;
  auth_type: 's2s_oauth' | 'user_oauth' | 'api_key';
  auth_client_id?: string;
  auth_client_secret?: string;
  auth_account_id?: string;
  auth_url?: string;
  auth_token_url?: string;
}

export interface UpdatePlatformRequest {
  name: string; // Required by backend
  api_url: string; // Required by backend
  is_production_enabled: boolean; // Required by backend
  auth_client_id?: string;
  auth_client_secret?: string;
  auth_account_id?: string;
}

export interface TestConnectionRequest {
  test_type?: 'basic' | 'full' | 'auth_only';
  timeout?: number;
  override_credentials?: {
    client_id?: string;
    client_secret?: string;
    account_id?: string;
  };
}

export interface TestConnectionResponse {
  status: 'success' | 'failed' | 'partial';
  platform: {
    id: number;
    name: string;
    platform_type: string;
  };
  connection_details: {
    auth_status: 'success' | 'failed' | 'not_configured';
    api_reachable: boolean;
    response_time_ms: number;
    api_version?: string;
    base_url: string;
    endpoints_tested: Array<{
      endpoint: string;
      method: string;
      status: 'success' | 'failed' | 'timeout';
      response_code: number;
      response_time_ms: number;
    }>;
  };
  diagnostics: {
    token_valid: boolean;
    token_expires_at?: string;
    rate_limit_remaining?: number;
    rate_limit_reset?: string;
    last_successful_call?: string;
    config_issues: string[];
  };
  test_summary: {
    total_tests: number;
    passed: number;
    failed: number;
    warnings: string[];
    errors: string[];
    test_duration_ms: number;
    timestamp: string;
  };
}

// Legacy payload for backward compatibility
export interface CreatePlatformPayload {
  name: string;
  platform_type: string;
  api_url: string;
  is_default: boolean;
}

class PlatformService {
  // OpenAPI-compliant methods
  async getAllPlatforms(params?: {
    platform_type?: 'zoom' | 'dialpad' | 'ring_central';
    is_production_enabled?: boolean;
  }): Promise<Platform[]> {
    try {
      const response = await apiClient.get<PlatformListResponse>('/api/platforms/', { params });
      return response.data.results;
    } catch (error) {
      console.error('Failed to fetch platforms:', error);
      throw this.handleError(error);
    }
  }

  async getPlatform(id: number): Promise<Platform> {
    try {
      const response = await apiClient.get<Platform>(`/api/platforms/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch platform ${id}:`, error);
      throw this.handleError(error);
    }
  }

  async createPlatform(data: CreatePlatformRequest): Promise<Platform> {
    try {
      const response = await apiClient.post<Platform>('/api/platforms/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create platform:', error);
      throw this.handleError(error);
    }
  }

  async updatePlatform(id: number, data: UpdatePlatformRequest): Promise<Platform> {
    try {
      const response = await apiClient.put<Platform>(`/api/platforms/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update platform ${id}:`, error);
      throw this.handleError(error);
    }
  }

  async deletePlatform(id: number): Promise<void> {
    try {
      await apiClient.delete(`/api/platforms/${id}/`);
    } catch (error) {
      console.error(`Failed to delete platform ${id}:`, error);
      throw this.handleError(error);
    }
  }

  async getAvailablePlatforms(): Promise<AvailablePlatformType[]> {
    try {
      const response = await apiClient.get<AvailablePlatformType[]>('/api/platforms/available_platforms/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch available platforms:', error);
      throw this.handleError(error);
    }
  }

  async testConnection(id: number, request?: TestConnectionRequest): Promise<TestConnectionResponse> {
    try {
      const response = await apiClient.post<TestConnectionResponse>(
        `/api/platforms/${id}/test-connection/`,
        request || { test_type: 'basic' }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to test connection for platform ${id}:`, error);
      throw this.handleError(error);
    }
  }

  // Legacy methods for backward compatibility
  async getPlatforms(): Promise<LegacyPlatform[]> {
    try {
      const response = await apiClient.get<{ results: LegacyPlatform[] } | LegacyPlatform[]>('/platforms/');
      if (response.data && 'results' in response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch platforms (legacy):', error);
      return [];
    }
  }

  async listExtractors(): Promise<Extractor[]> {
    try {
      const response = await apiClient.get<Extractor[]>('/platforms/extractors/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch extractors:', error);
      return [];
    }
  }

  async createPlatformLegacy(payload: CreatePlatformPayload): Promise<Platform | null> {
    try {
      const response = await apiClient.post<Platform>('/platforms/', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to create platform (legacy):', error);
      return null;
    }
  }

  async deletePlatformLegacy(platformId: number): Promise<boolean> {
    try {
      await apiClient.delete(`/platforms/${platformId}/`);
      return true;
    } catch (error) {
      console.error(`Failed to delete platform ${platformId} (legacy):`, error);
      return false;
    }
  }

  async getPlatformById(platformId: number): Promise<Platform | null> {
    try {
      const response = await apiClient.get<Platform>(`/platforms/${platformId}/`);
      const platform = response.data;

      // Attempt to fetch specification details for this platform
      try {
        const specResponse = await apiClient.get(`/specs/${platform.id}/`);
        if (specResponse.data && specResponse.status === 200 && specResponse.data.id) {
          (platform as any).specification = {
            id: specResponse.data.id,
            name: specResponse.data.name
          };
        }
      } catch (specError) {
        // Try fallback for specification
        try {
          const fallbackResponse = await apiClient.get(`/specs/${platform.id}`);
          if (fallbackResponse.data && fallbackResponse.status === 200) {
            (platform as any).specification = {
              id: fallbackResponse.data.id,
              name: fallbackResponse.data.name
            };
          }
        } catch (fallbackError) {
          (platform as any).specification = null;
        }
      }

      // Ensure required fields are present
      if (typeof platform.is_default === 'undefined') {
        (platform as any).is_default = false;
      }
      if (typeof platform.group === 'undefined') {
        (platform as any).group = null;
      }
      return platform;
    } catch (error) {
      console.error(`Failed to fetch platform ${platformId}:`, error);
      return null;
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      if (errorData && typeof errorData === 'object' && 'error' in errorData) {
        // OpenAPI Error schema format
        return new Error(errorData.error);
      }
      return new Error(`HTTP ${error.response.status}: ${error.response.statusText}`);
    }
    return error instanceof Error ? error : new Error('Unknown error occurred');
  }
}

export const platformService = new PlatformService();

// Export legacy object for backward compatibility
export const legacyPlatformService = {
  getPlatforms: platformService.getPlatforms.bind(platformService),
  listExtractors: platformService.listExtractors.bind(platformService),
  createPlatform: platformService.createPlatformLegacy.bind(platformService),
  deletePlatform: platformService.deletePlatformLegacy.bind(platformService),
  getPlatformById: platformService.getPlatformById.bind(platformService)
};
